import { isEqual } from "lodash-es";
import { type FC, type Ref, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useBoolean } from "usehooks-ts";

import { roleIcon } from "components/materials/home/roles";
import { SectionRoleType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { ChevronDownIcon, PersonSingleIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store";
import {
  selectAllCourseUsersByUserId,
  selectCourseCodes,
  selectCoursesByTermAndCourseCode,
  selectTerms,
  selectUserById,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { COURSE_ROLES, getCourseResourceRole } from "utils/section";
import { getUserFullName } from "utils/user-utils";

export interface UserEnrollmentSubMenuProps {
  userId: string;
  closeMenu?: () => void;
}

type Inputs = {
  courseCodeId: string | null;
  termId: string | null;
  courseIds: Set<string>;
  newRole: SectionRoleType;
};

const updateEnrollments = async (
  userId: string,
  courseUserIdsToEnable: string[],
  courseUserIdsToDisable: string[],
  courseIdsToAdd: string[],
  role: SectionRoleType,
) => {
  const createPromises = courseIdsToAdd.map((courseId) =>
    storeApi.course_users.create(
      {
        section_id: courseId,
        user_id: userId,
        role,
      },
      { skipToast: true },
    ),
  );
  const enablePromises = courseUserIdsToEnable.map((courseUserId) =>
    storeApi.course_users.partial_update(
      { id: courseUserId, is_disabled: false, role },
      { skipToast: true },
    ),
  );
  const disablePromises = courseUserIdsToDisable.map((courseUserId) =>
    storeApi.course_users.partial_update(
      { id: courseUserId, is_disabled: true },
      { skipToast: true },
    ),
  );
  return Promise.all([...createPromises, ...enablePromises, ...disablePromises])
    .then(() =>
      toastSuccessMessage(t("user_options_menu.update_enrolled_courses.update_enrollment_success")),
    )
    .catch(() => toastLocalizedOperationError("update_enrollment_failure"));
};

export const UserEnrollmentSubMenu: FC<UserEnrollmentSubMenuProps> = ({
  userId,
  closeMenu = () => {},
}) => {
  const user = useAppSelector((s) => selectUserById(s, userId));
  const courseUsers = useListSelector((s) => selectAllCourseUsersByUserId(s, userId));
  const enabledCourseUsers = courseUsers.filter((cu) => !cu.is_disabled);
  const currentlyEnrolledCourseIds = new Set(enabledCourseUsers.map((cu) => cu.section_id));

  const courseCodes = useAppSelector(selectCourseCodes);
  const orderedCourseCodes = courseCodes.toSorted(ORDER_BY_TITLE);
  const courseCodeMenuRef: Ref<MenuInstance | null> = useRef(null);

  const terms = useAppSelector(selectTerms);
  const termMenuRef: Ref<MenuInstance | null> = useRef(null);

  const roleMenuRef: Ref<MenuInstance | null> = useRef(null);
  const { value: shouldSelect, toggle: toggleSelectButton } = useBoolean(true);
  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const { setValue, handleSubmit, watch, control } = useForm<Inputs>({
    defaultValues: {
      courseCodeId: null,
      termId: null,
      courseIds: currentlyEnrolledCourseIds,
      newRole: "student",
    },
  });

  const courseCodeId = watch("courseCodeId");
  const termId = watch("termId");
  const courseIds = watch("courseIds");
  const newRole = watch("newRole");

  const hasChanges = !isEqual(courseIds, currentlyEnrolledCourseIds);
  const courseIdsToDisable = new Set(
    [...currentlyEnrolledCourseIds].filter((x) => !courseIds.has(x)),
  );
  const requestedCourseIds = new Set(
    [...courseIds].filter((x) => !currentlyEnrolledCourseIds.has(x)),
  );

  const onSubmit: SubmitHandler<Inputs> = () => {
    if (courseIdsToDisable.size > 0) {
      openConfirmationDialog();
    } else {
      updateEnrollmentsFromForm();
      closeMenu();
    }
  };

  const courses = useAppSelector((s) => selectCoursesByTermAndCourseCode(s, termId, courseCodeId));

  // This is for the confirmation dialog, which shows the names of removed courses
  const disabledCourseNames = Array.from(courseIdsToDisable)
    .sort()
    .map(
      (disabledCourseId) => courses.find((course) => course.id === disabledCourseId)?.title || "",
    );

  const updateEnrollmentsFromForm = () => {
    const disabledCourseUsers = courseUsers.filter((cu) => cu.is_disabled);

    // Adding enrollments is a bit complicated; for each course, we need to figure out
    // if the user is already enrolled but disabled, or if they are not enrolled at all.
    const courseUsersToEnable = disabledCourseUsers.filter((cu) =>
      requestedCourseIds.has(cu.section_id),
    );
    const courseIdsToAdd = Array.from(requestedCourseIds).filter(
      (courseId) => !courseUsersToEnable.some((cu) => cu.section_id === courseId),
    );

    // Removing enrollments is simpler; we just disable the CourseUser object.
    const courseUsersToDisable = enabledCourseUsers.filter((cu) =>
      courseIdsToDisable.has(cu.section_id),
    );
    return updateEnrollments(
      userId,
      courseUsersToEnable.map((cu) => cu.id),
      courseUsersToDisable.map((cu) => cu.id),
      courseIdsToAdd,
      newRole,
    );
  };

  if (!user) {
    return <div className="p-3">{t("common.loading")}</div>;
  }

  return (
    <form className="w-full min-w-[400px] bg-black-tint-99 p-3">
      <h3 className="body-s flex gap-1 pb-5">
        {t("user_options_menu.update_enrolled_courses.title", { name: user.first_name })}
      </h3>

      <div className="flex items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name="courseCodeId"
            render={({ field }) => {
              const options = [
                { id: null, title: t("import_modal.all_course_codes") },
                ...orderedCourseCodes,
              ] as { id: string | null; title: string }[];
              const selectedTitle =
                options.find((o) => o.id === field.value)?.title ||
                t("import_modal.all_course_codes");

              return (
                <SubMenu
                  className="dropdown"
                  direction="bottom"
                  instanceRef={courseCodeMenuRef}
                  label={
                    <Button kind="white" size="s">
                      <IconText
                        iconEnd={<ChevronDownIcon />}
                        iconStart={<TextBooksIcon />}
                        text={<span className="max-w-[600px] truncate">{selectedTitle}</span>}
                      />
                    </Button>
                  }
                  openTrigger="clickOnly"
                  position="initial"
                >
                  <MenuRadioGroup {...field}>
                    {options.map((option) => (
                      <MenuItem
                        key={option.id}
                        type="radio"
                        value={option.id}
                        onClick={(e) => {
                          field.onChange(option.id);
                          e.keepOpen = true; // eslint-disable-line no-param-reassign
                          courseCodeMenuRef.current?.closeMenu();
                        }}
                      >
                        <IconText
                          iconStart={<TextBooksIcon />}
                          text={<span className="max-w-[600px] truncate">{option.title}</span>}
                        />
                      </MenuItem>
                    ))}
                  </MenuRadioGroup>
                </SubMenu>
              );
            }}
          />

          <Controller
            control={control}
            name="termId"
            render={({ field }) => {
              const options = [{ id: null, title: t("import_modal.all_terms") }, ...terms] as {
                id: string | null;
                title: string;
              }[];
              const selectedTitle =
                options.find((o) => o.id === field.value)?.title || t("import_modal.all_terms");

              return (
                <SubMenu
                  className="dropdown"
                  direction="bottom"
                  instanceRef={termMenuRef}
                  label={
                    <Button kind="white" size="s">
                      <IconText
                        iconEnd={<ChevronDownIcon />}
                        iconStart={<TextBooksIcon />}
                        text={<span className="max-w-[600px] truncate">{selectedTitle}</span>}
                      />
                    </Button>
                  }
                  openTrigger="clickOnly"
                  position="initial"
                >
                  <MenuRadioGroup {...field}>
                    {options.map((option) => (
                      <MenuItem
                        key={option.id}
                        type="radio"
                        value={option.id}
                        onClick={(e) => {
                          field.onChange(option.id);
                          e.keepOpen = true; // eslint-disable-line no-param-reassign
                          termMenuRef.current?.closeMenu();
                        }}
                      >
                        <IconText
                          iconStart={<TextBooksIcon />}
                          text={<span className="max-w-[600px] truncate">{option.title}</span>}
                        />
                      </MenuItem>
                    ))}
                  </MenuRadioGroup>
                </SubMenu>
              );
            }}
          />
        </div>

        <Button
          kind="white"
          size="s"
          onClick={() => {
            const newCourseIds = new Set(courseIds);
            courses.forEach((course) => {
              if (shouldSelect) {
                newCourseIds.add(course.id);
              } else {
                newCourseIds.delete(course.id);
              }
            });
            setValue("courseIds", newCourseIds);
            toggleSelectButton();
          }}
        >
          {shouldSelect ? t("common.select_all") : t("common.unselect_all")}
        </Button>
      </div>

      <div className="mb-2 max-h-[150px] overflow-y-scroll rounded-lg border border-black-tint-90 p-2">
        {/* TODO: add empty state if the selected and term and course code has no courses */}
        {courses.length === 0 ? (
          <div className="text-black-tint-70">{t("copy_to_course_sub_menu.no_courses")}</div>
        ) : (
          <ul>
            {courses.map((course) => (
              <li key={course.id}>
                <label className="flex items-center gap-2 py-1">
                  <input
                    checked={courseIds.has(course.id)}
                    type="checkbox"
                    onChange={(e) => {
                      const newCourseIds = new Set(courseIds);
                      if (e.target.checked) {
                        newCourseIds.add(course.id);
                      } else {
                        newCourseIds.delete(course.id);
                      }
                      setValue("courseIds", newCourseIds);
                    }}
                  />
                  <span className="max-w-[600px] truncate">{course.title}</span>
                  {currentlyEnrolledCourseIds.has(course.id) && (
                    <span className="italic text-black-tint-55">
                      ({t("user_options_menu.update_enrolled_courses.already_enrolled")})
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="right-align pb-2 text-black-tint-70">
        {t("user_options_menu.update_enrolled_courses.selection_status", {
          count: requestedCourseIds.size,
          existing_count: currentlyEnrolledCourseIds.size,
        })}
      </div>

      <div className="flex items-center justify-between">
        <div>{t("user_options_menu.update_enrolled_courses.role_in_new_course")}</div>
        <Controller
          control={control}
          name="newRole"
          render={({ field }) => {
            const resourceRole = getCourseResourceRole(field.value);
            const selectedTitle = t(`roles.course_${resourceRole}`) || "Unknown Role";
            const selectedIcon = roleIcon[field.value] || <PersonSingleIcon />;

            return (
              <SubMenu
                className="dropdown"
                direction="top"
                instanceRef={roleMenuRef}
                label={
                  <Button kind="white" size="s">
                    <IconText
                      iconEnd={<ChevronDownIcon />}
                      iconStart={selectedIcon}
                      text={selectedTitle}
                    />
                  </Button>
                }
                openTrigger="clickOnly"
                position="initial"
              >
                <MenuRadioGroup {...field}>
                  {COURSE_ROLES.map((role) => (
                    <MenuItem
                      key={role}
                      type="radio"
                      value={role}
                      onClick={(e) => {
                        field.onChange(role);
                        e.keepOpen = true; // eslint-disable-line no-param-reassign
                        roleMenuRef.current?.closeMenu();
                      }}
                    >
                      <IconText
                        iconStart={roleIcon[role] || <PersonSingleIcon />}
                        text={t(`roles.course_${getCourseResourceRole(role)}`)}
                      />
                    </MenuItem>
                  ))}
                </MenuRadioGroup>
              </SubMenu>
            );
          }}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button kind="white" size="s" onClick={closeMenu}>
          {t("common.cancel")}
        </Button>

        <Button disabled={!hasChanges} kind="blue" size="s" onClick={handleSubmit(onSubmit)}>
          {t("user_options_menu.update_enrolled_courses.confirm")}
        </Button>
      </div>

      <ConfirmationDialog
        confirmButtonKind="red"
        confirmButtonText={t("user_options_menu.update_enrolled_courses.remove_from_course", {
          count: courseIdsToDisable.size,
        })}
        description={
          <div className="sr-only">
            {t("user_options_menu.update_enrolled_courses.confirmation_dialog_title", {
              count: courseIdsToDisable.size,
              name: getUserFullName(user),
            })}
          </div>
        }
        open={confirmationDialogIsOpen}
        title={
          <div className="font-bold">
            {t("user_options_menu.update_enrolled_courses.are_you_sure")}
          </div>
        }
        onClose={closeConfirmationDialog}
        onConfirm={() => {
          updateEnrollmentsFromForm();
          closeMenu();
        }}
      >
        <div className="my-6 border-l-2 border-red px-4 font-semibold">
          {t("user_options_menu.update_enrolled_courses.confirmation_dialog_body", {
            count: courseIdsToDisable.size,
            userName: getUserFullName(user),
            courseNames: disabledCourseNames.map((name) => `“${name}”`),
          })}
        </div>
      </ConfirmationDialog>
    </form>
  );
};
